// The store's orders: where they stand, which ones they are, and one of them
// open on a page that can be printed and put in the parcel. Admin → Store →
// Orders shows the whole store's (aldoo-na `store {orders}`); a project shows
// its own scope by passing its own `api` (aldoo.com: a seller's orders), its
// words (`useTexts`) and what it knows of delivery (`renderPickup`).
//
// The tiles are the states an order passes through, and they are the
// filter: clicking one narrows the table to it, clicking it again lets it go.
import React, { useMemo, useState } from "react"
import DataTable from "aldoo-ra/DataTable"
import DateFilterDropdown from "aldoo-ra/DateFilterDropdown"
import moment from "moment"
import { Section, Chip, Empty, money, dateOnly, timeAgo, placedAt, orderState, presaleOf, awaitingShipment, PRESALE } from "./ui"
import OrderPage from "./OrderPage"
import useStoreEvents from "aldoo-ra/Store/useStoreEvents"
import { useUrlParam } from "aldoo-ra/URLState"
import useOrderTexts from "./useOrderTexts"
import { storeOrdersAPI } from "./api"
import { showNotification } from "aldoo-ra/Notification"
import { DocumentDuplicateIcon } from "@heroicons/react/24/outline"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

const TILES = [
  { key: "paid", label: "Open", hint: "Waiting to be sent", color: "#3b6fd4" },
  { key: "shipping", label: "Shipping", hint: "On its way", color: "#e8a33d" },
  { key: "completed", label: "Completed", hint: "Delivered and done", color: "#2f9e63" },
  { key: "canceled", label: "Canceled", hint: "Called off", color: "#cc4436" },
  // a buyer asks to call off an order not yet sent: answered before it ships
  { key: "cancel_requested", label: "Cancellation requests", hint: "Waiting for your answer: do not send them", color: "#c2410c" },
  // a buyer's return still in play: waiting for the seller's answer, or for its items to come back
  { key: "return_requested", label: "Return requests", hint: "Waiting for your answer or for the items", color: "#d9822b" },
  // paid on delivery, the buyer asked for an invoice, none on the order yet (the seller uploads it):
  // shown while there are such orders (whenAny) — a store with no payment on delivery never sees it
  { key: "awaiting_invoice", label: "Awaiting invoice", hint: "Paid on delivery, an invoice asked for", color: "#0e7490", whenAny: true },
  // the goods came back — listed whatever the money did, so a returned order refunded in full is in both
  { key: "returned", label: "Returned", hint: "The goods came back", color: "#6b5ca5" },
  // the money has states the fulfilment does not
  { key: "partly_refunded", label: "Partly refunded", hint: "Some of it given back", color: "#b0742a" },
  { key: "refunded", label: "Refunded", hint: "All of it given back", color: "#8a3ab0" },
]

/** A range the API understands, from what the dropdown answers with. */
// the dropdown's own presets, the same ids and ranges, so their names can be
// said in the site language (DateFilterDropdown takes them as customPresets)
const PRESETS = [
  { id: "last24h", label: "Last 24 Hours", value: { days: 1 } },
  { id: "last7d", label: "Last 7 Days", value: { days: 7 } },
  { id: "last30d", label: "Last 30 Days", value: { days: 30 } },
  { id: "last90d", label: "Last 90 Days", value: { days: 90 } },
  { id: "last365d", label: "Last 365 Days", value: { days: 365 } },
  { id: "lifetime", label: "Lifetime", value: null },
  { id: "custom", label: "Custom Range", value: "custom" },
]

const rangeOf = (filter) => {
  const end = moment().endOf("day").toDate()
  if (filter?.type === "custom" && filter.range?.startDate && filter.range?.endDate) {
    return { startDate: filter.range.startDate, endDate: filter.range.endDate }
  }
  const days = filter?.value?.days
  // an order list without a range is the whole book, which is what a vendor
  // looking for one order wants
  if (!days) return { startDate: moment("2000-01-01").toDate(), endDate: end }
  return { startDate: moment().subtract(days, "days").startOf("day").toDate(), endDate: end }
}

/** The table's search object as the term the API takes. */
const searchTermOf = (search) => {
  if (!search) return ""
  if (typeof search === "string") return search
  const values = Object.values(search).flatMap((value) => (Array.isArray(value) ? value.flatMap((entry) => Object.values(entry)) : [value]))
  return values.find((value) => typeof value === "string" && value.trim()) || ""
}

// the units left once the refunds took theirs (the server's `final`, aldoo-na/store/refunds netOf), else the cart's
const itemCount = (order) => order.final?.itemCount ?? (order.cart || []).reduce((sum, item) => sum + (item.quantity ?? 1), 0)
/** What the order came to after its refunds. */
const netOf = (order) => order.final?.net ?? Math.max(0, (order.amount ?? 0) - (order.refundedAmount ?? 0))

/** What the buyer was given off: the line discounts, plus any promo code. */
const discountOf = (order) => {
  const lines = order.cart || []
  const full = lines.reduce((sum, item) => sum + ((item.originalPrice ?? item.price) ?? 0) * (item.quantity ?? 1), 0)
  const paid = lines.reduce((sum, item) => sum + (item.price ?? 0) * (item.quantity ?? 1), 0)
  return Math.max(0, full - paid) + (order.discountAmount ?? 0)
}

/** What kind of order a framework order is, beside its number: a subscription's payment or renewal. */
// a buyer asked to call the order off and the vendor has not answered yet
const cancelRequested = (order) => (order.cancelRequests || []).some((request) => request.status === "open")

// such an order's row is tinted red, so it is not sent by mistake
const ROW_STYLES = { getRowStyle: (index, order) => (cancelRequested(order) ? "bg-admin_error/[0.06] hover:bg-admin_error/10" : "") }

const kindOf = (order) => (order.origin === "subscription" ? (order.subscription?.renewal ? "Subscription renewal" : "Subscription payment") : null)

/**
 * `orgId`: the seller whose orders these are (the live updates follow it;
 * null: the whole store). `api`: { list, get, ship, setStatus, refund } (./api.js).
 * `useTexts`: a hook answering t(). `locale`: how dates are written.
 * `renderQR(url)`: a QR code (the project's library) — the printed sheet's way into the buyer's order.
 * `renderPickup({ order, office, provider, t, locale })`: the pickup office of
 * a parcel, as the project shows it (a map, the courier's ids).
 * `helpKeys`: the help center's articles behind the "?" hints (aldoo-ra
 * HelpCenter/InPlaceHelp), named by the project: { orders, states, order,
 * ship, cancel, returns, refund, invoice } — each a help key, or absent.
 */
export default function StoreOrders({ orgId = null, api = storeOrdersAPI, useTexts = useOrderTexts, locale = "en-GB", renderPickup, renderQR, renderShip, renderShipment, renderOfficePicker, extraPrints, helpKeys = {}, viewer = "admin" }) {
  const t = useTexts()
  const presets = useMemo(() => PRESETS.map((preset) => ({ ...preset, label: t(preset.label) })), [t])
  const [filter, setFilter] = useState({ type: "preset", value: { days: 30 } })
  // the orders that need doing are the ones a vendor opens this for; clicking
  // the tile again shows every order
  const [status, setStatus] = useState("paid")
  const [counts, setCounts] = useState(null)
  // the open order is in the url (?order=), so a link — the new-order mail's
  // "manage" — lands on it, and a refresh keeps it
  const [openOrder, setOpenOrder] = useUrlParam("order", { default: "" })
  const [refreshKey, setRefreshKey] = useState(0)

  // an order placed, shipped or refunded while the list is open shows itself
  useStoreEvents({ orgId, onEvent: () => setRefreshKey((key) => key + 1) })

  const range = useMemo(() => rangeOf(filter), [filter])

  const loadPage = async ({ page, itemsPerPage, search, sort }) => {
    const order_by = sort?.field ? `${sort.order === "asc" ? "" : "-"}${sort.field}` : "-completedAt"
    const res = await api.list({
      company: orgId,
      dateRange: range,
      page,
      items_per_page: itemsPerPage,
      order_by,
      search: searchTermOf(search),
      orderStatus: status || undefined,
      withCounts: true,
    })
    if (res?.counts) setCounts(res.counts)
    return { data: res?.data || [], total: res?.total ?? 0 }
  }

  const refresh = () => setRefreshKey((key) => key + 1)

  /** The order number into the clipboard, said as the order dialog says it. */
  const copyId = async (value) => {
    try {
      await navigator.clipboard.writeText(value)
      showNotification({ message: t`Order ID copied`, className: "bg-admin_success text-white" })
    } catch {
      showNotification({ message: t`Could not copy`, className: "bg-admin_error text-white" })
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Section
        title={
          <span className="inline-flex items-center gap-1.5">
            {t`Orders`}
            {/* the help center's article on the orders, where they are (helpKeys.orders) */}
            <HelpHint helpKey={helpKeys.orders} iconClassName="w-[18px] h-[18px]" />
          </span>
        }
        actions={<div className="w-full md:w-[230px]"><DateFilterDropdown variant="admin" showLabel={false} defaultPreset="last30d" customPresets={presets} value={filter} onChange={setFilter} /></div>}
      >
        {/* the states an order passes through, and the filter at the same time */}
        {/* what the states mean, from the help center, at the row's corner (helpKeys.states) */}
        {helpKeys.states && (
          <div className="flex justify-end -mb-2">
            <HelpHint helpKey={helpKeys.states} align="end" />
          </div>
        )}
        {/* as many in a row as the width holds: every state in one row on a desktop */}
        <div className="grid grid-cols-2 md:grid-cols-[repeat(auto-fit,minmax(128px,1fr))] gap-3">
          {TILES.filter((tile) => !tile.whenAny || status === tile.key || counts?.[tile.key] > 0).map((tile) => {
            const on = status === tile.key
            return (
              <button
                key={tile.key}
                onClick={() => setStatus(on ? "" : tile.key)}
                // the count held to the bottom left, on one line across the row whatever the labels wrap to
                className={`h-full flex flex-col justify-between gap-1 rounded-lg px-4 py-3 text-left transition-colors ${on ? "text-white" : "border border-admin_bordered text-admin_text hover:bg-admin_neutral_1/50"}`}
                style={on ? { background: tile.color } : undefined}
                aria-pressed={on}
                title={on ? t`Show every order again` : t(tile.hint)}
              >
                <div className={`text-xs flex items-start gap-1.5 ${on ? "text-white/80" : "text-admin_text_secondary"}`}>
                  <span className="inline-block w-2 h-2 mt-[5px] shrink-0 rounded-full" style={{ background: on ? "rgba(255,255,255,0.9)" : tile.color }} />
                  {t(tile.label)}
                </div>
                <div className={`text-2xl font-semibold tabular-nums ${on ? "text-white" : "text-admin_text"}`}>
                  {counts ? counts[tile.key] ?? 0 : <span className="text-admin_text_secondary">…</span>}
                </div>
              </button>
            )
          })}
        </div>

        <DataTable
          variant="admin"
          styleOverrides={ROW_STYLES}
          apiMethod={loadPage}
          apiDataField="data"
          itemIdField="_id"
          refreshKey={`${orgId}:${range.startDate}:${range.endDate}:${status}:${refreshKey}`}
          itemsPerPage={10}
          searchPlaceholder={t`Order number, email, phone, product, tracking number…`}
          searchField="orderId email"
          // a search looks through every order: the state tile and the period step aside, so
          // an order outside them (completed, last year) still turns up
          onSearchTermChange={(term) => {
            if (!String(term || "").trim()) return
            setStatus("")
            setFilter((current) => (current?.type === "preset" && current?.value === null ? current : { type: "preset", value: null, id: "lifetime" }))
          }}
          onRowClick={(order) => setOpenOrder(order.orderId || order._id)}
          renders={[
            {
              id: "orderId",
              title: t`Order`,
              sortable: true,
              headerClassName: "w-[130px]",
              render: (orderId, order) => (
                <span className="block">
                  {/* the number copies itself, as in the order dialog; the rest of the row opens the order */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      copyId(orderId || String(order._id).slice(-8).toUpperCase())
                    }}
                    title={t`Copy order ID`}
                    className="group/copy inline-flex items-center gap-1 font-medium text-admin_text hover:text-admin_primary"
                    data-testid="orders-copy-id"
                  >
                    {orderId || String(order._id).slice(-8).toUpperCase()}
                    <DocumentDuplicateIcon className="w-3.5 h-3.5 opacity-0 group-hover/copy:opacity-100 transition-opacity" aria-hidden />
                  </button>
                  {kindOf(order) && <span className="block text-xs text-admin_text_secondary">{t(kindOf(order))}</span>}
                </span>
              ),
            },
            {
              // who bought it: the name on the order, how to reach them
              id: "email",
              title: t`Customer`,
              render: (email, order) => {
                const address = order.address || order.shippingAddress || order.billingAddress || {}
                const name = String(address.name || "").trim()
                const phone = address.phone || order.shippingAddress?.phone || order.billingAddress?.phone || ""
                return (
                  <div className="min-w-0" data-testid="order-customer">
                    <div className="text-admin_text truncate">{name || email || "—"}</div>
                    {name && email && <div className="text-xs text-admin_text_secondary truncate">{email}</div>}
                    {phone && <div className="text-xs text-admin_text_secondary truncate">{phone}</div>}
                  </div>
                )
              },
            },
            {
              // when it was paid: the transaction is made when the checkout
              // opens, which can be a day before the payment
              id: "completedAt",
              title: t`Placed`,
              sortable: true,
              headerClassName: "w-[140px]",
              render: (value, order) => {
                const placed = placedAt(order)
                return (
                  <span className="block">
                    <span className="block text-admin_text">{dateOnly(placed, locale)}</span>
                    <span className="block text-xs text-admin_text_secondary">{timeAgo(placed, t)}</span>
                  </span>
                )
              },
            },
            { id: "items", title: t`Items`, headerClassName: "w-[90px] text-right", render: (_, order) => <span className="block text-right tabular-nums text-admin_text">{itemCount(order)}</span> },
            {
              id: "amount",
              title: t`Total`,
              sortable: true,
              headerClassName: "w-[130px] text-right",
              render: (amount, order) => {
                const discount = discountOf(order)
                return (
                  <span className="block text-right tabular-nums text-admin_text">
                    {/* what it came to after its refunds; what was paid, struck through beside it */}
                    {money(netOf(order), order.currency)}
                    {order.refundedAmount > 0 && <span className="block text-xs text-admin_text_secondary line-through">{money(amount, order.currency)}</span>}
                    {discount > 0 && (
                      <span className="block text-xs text-admin_text_secondary" title={order.promoCode ? t("Promo code %s", order.promoCode) : t`Discounted lines`}>
                        {t("−%s off", money(discount, order.currency))}
                      </span>
                    )}
                    {order.refundedAmount > 0 && <span className="block text-xs text-admin_error">{t("−%s refunded", money(order.refundedAmount, order.currency))}</span>}
                  </span>
                )
              },
            },
            {
              id: "orderStatus",
              title: t`Status`,
              sortable: true,
              headerClassName: "w-[150px]",
              render: (value, order) => {
                const state = orderState(order)
                // an order can stand in several states at once — its goods back and its money back,
                // some of either, a request in play: a chip each, side by side
                const refundState = order.final?.state || (order.refundedAmount > 0 ? (order.refundedAmount >= order.amount ? "full" : "partial") : "none")
                const extra = [
                  // the money, when the main state speaks of the goods (returned) or of how far it got
                  state.label !== "refunded" && refundState === "full" && { key: "refunded", label: "refunded", cls: "bg-admin_error/10 text-admin_error" },
                  refundState === "partial" && { key: "partly_refunded", label: "partly refunded", cls: "bg-[#b0742a]/10 text-[#b0742a]" },
                  order.final?.returned === "partial" && { key: "partly_returned", label: "partly returned", cls: "bg-[#6b5ca5]/10 text-[#6b5ca5]" },
                  cancelRequested(order) && { key: "cancel_requested", label: "cancellation requested", cls: "bg-admin_error/10 text-admin_error" },
                  order.final?.openRequests > 0 && { key: "return_requested", label: "return requested", cls: "bg-admin_warning/20 text-admin_text" },
                  !(order.final?.openRequests > 0) && order.final?.awaitingItems > 0 && { key: "waiting_for_items", label: "waiting for items", cls: "bg-admin_warning/20 text-admin_text" },
                  order.final?.awaitingInvoice && { key: "awaiting_invoice", label: "awaiting invoice", cls: "bg-[#0e7490]/10 text-[#0e7490]" },
                  // not sent yet and holding items not out yet: it waits for their release
                  awaitingShipment(order) && presaleOf(order) && { key: "preorder", label: "Pre-order", cls: PRESALE },
                ].filter(Boolean)
                return (
                  <div className="flex flex-wrap items-center gap-1" data-testid="order-states">
                    <Chip className={state.className}>{t(state.label)}</Chip>
                    {extra.map((chip) => (
                      <Chip key={chip.key} className={chip.cls}>{t(chip.label)}</Chip>
                    ))}
                  </div>
                )
              },
            },
          ]}
          noData={<Empty>{t`No orders in this period`}</Empty>}
        />
      </Section>

      {openOrder && (
        <OrderPage
          orgId={orgId}
          orderId={openOrder}
          viewer={viewer}
          api={api}
          useTexts={useTexts}
          locale={locale}
          renderPickup={renderPickup}
          renderShip={renderShip}
          renderShipment={renderShipment}
          renderOfficePicker={renderOfficePicker}
          extraPrints={extraPrints}
          renderQR={renderQR}
          helpKeys={helpKeys}
          onClose={() => setOpenOrder("")}
          // another order of the same buyer (the Client tab's list) opens in its place
          onOpenOrder={(other) => setOpenOrder(other)}
          onChanged={() => {
            refresh()
          }}
        />
      )}
    </div>
  )
}
